/* AUTO-SYNCED from PsyKit.io/components/audio/AudioEngine.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

// ── Preset definitions ──────────────────────────────────────────────────────
// Binaural beats: left ear hears `carrier`, right ear hears `carrier + beat`.
// The brain perceives the difference frequency as the "beat" — that beat Hz
// entrains brainwave activity toward the target state.
export type AudioPreset = "theta" | "alpha" | "delta" | "off";

export const AUDIO_PRESETS: Record<
  Exclude<AudioPreset, "off">,
  { carrier: number; beat: number; label: string; desc: string }
> = {
  theta: { carrier: 200, beat: 6,  label: "Theta", desc: "6 Hz · deep relaxation" },
  alpha: { carrier: 210, beat: 10, label: "Alpha", desc: "10 Hz · calm focus"     },
  delta: { carrier: 180, beat: 2,  label: "Delta", desc: "2 Hz · deep rest"       },
};

// ── Audio graph shape ────────────────────────────────────────────────────────
interface AudioGraph {
  ctx: AudioContext;
  /** Left-channel oscillator — plays `carrier` Hz into the left ear. */
  oscL: OscillatorNode;
  /** Right-channel oscillator — plays `carrier + beat` Hz into the right ear. */
  oscR: OscillatorNode;
  /** Per-channel amplitude: 1 − xNorm (loud at left edge, silent at right). */
  gainL: GainNode;
  /** Per-channel amplitude: xNorm (silent at left edge, loud at right edge). */
  gainR: GainNode;
  /** Overall volume + fade ramps. */
  masterGain: GainNode;
}

function buildGraph(carrier: number, beat: number): AudioGraph {
  const ctx = new AudioContext();

  const oscL = ctx.createOscillator();
  const oscR = ctx.createOscillator();
  oscL.type = "sine";
  oscR.type = "sine";
  oscL.frequency.value = carrier;
  oscR.frequency.value = carrier + beat;

  const gainL = ctx.createGain();
  const gainR = ctx.createGain();
  gainL.gain.value = 0.5; // neutral centre start
  gainR.gain.value = 0.5;

  const masterGain = ctx.createGain();
  masterGain.gain.value = 0; // start silent — faded in on start

  // ChannelMergerNode routes L and R oscillators into separate stereo channels,
  // preserving the distinct frequencies needed for binaural beat perception.
  const merger = ctx.createChannelMerger(2);

  oscL.connect(gainL);
  gainL.connect(merger, 0, 0); // → left channel
  oscR.connect(gainR);
  gainR.connect(merger, 0, 1); // → right channel
  merger.connect(masterGain);
  masterGain.connect(ctx.destination);

  oscL.start();
  oscR.start();

  return { ctx, oscL, oscR, gainL, gainR, masterGain };
}

/** Fully tears down a graph — stops oscillators, closes the AudioContext. */
function teardownGraph(graph: AudioGraph) {
  try { graph.oscL.stop(); } catch { /* already stopped */ }
  try { graph.oscR.stop(); } catch { /* already stopped */ }
  graph.ctx.close().catch(() => {});
}

// ── Public imperative handle ─────────────────────────────────────────────────
export interface AudioEngineHandle {
  /**
   * Update stereo panning to match the ball's normalized position (0–1).
   * Called at 60fps by the animation loop — imperative to avoid React re-renders.
   * gainL = 1 − xNorm (left ear dominant when ball is left)
   * gainR = xNorm      (right ear dominant when ball is right)
   */
  updateXNorm: (xNorm: number) => void;
}

// ── Props ────────────────────────────────────────────────────────────────────
interface AudioEngineProps {
  isRunning: boolean;
  audioOn: boolean;
  /** Master volume 0–1. */
  volume: number;
  preset: AudioPreset;
}

// ── Component ────────────────────────────────────────────────────────────────
const AudioEngine = forwardRef<AudioEngineHandle, AudioEngineProps>(
  function AudioEngine({ isRunning, audioOn, volume, preset }, ref) {
    const graphRef = useRef<AudioGraph | null>(null);
    const activeRef = useRef(false); // true only while audio is audibly playing

    // Expose imperative xNorm updater — zero React overhead at 60fps
    useImperativeHandle(ref, () => ({
      updateXNorm(xNorm: number) {
        if (!activeRef.current || !graphRef.current) return;
        const { gainL, gainR, ctx } = graphRef.current;
        const t = ctx.currentTime;
        // setTargetAtTime with a short time constant gives smooth,
        // click-free crossfade at each frame (~16ms at 60fps).
        gainL.gain.setTargetAtTime(1 - xNorm, t, 0.016);
        gainR.gain.setTargetAtTime(xNorm, t, 0.016);
      },
    }));

    // ── Start / stop audio ───────────────────────────────────────────────────
    useEffect(() => {
      const shouldPlay = isRunning && audioOn && preset !== "off";
      // cancelled flag prevents a pending ctx.resume().then() from starting
      // audio after the effect has already been superseded (e.g. stop was hit
      // before the async resume resolved — the classic race condition).
      let cancelled = false;

      if (shouldPlay) {
        const { carrier, beat } = AUDIO_PRESETS[preset as Exclude<AudioPreset, "off">];

        // Always build a fresh graph on each play — guarantees a clean AudioContext
        // with no leftover state from a previous session.
        if (graphRef.current) {
          teardownGraph(graphRef.current);
        }
        const graph = buildGraph(carrier, beat);
        graphRef.current = graph;

        // Resume the new context (required after user gesture on some browsers)
        graph.ctx.resume().then(() => {
          if (cancelled) return; // stop was called before resume resolved — abort
          const t = graph.ctx.currentTime;
          // Fade in over 200ms — prevents click on start
          graph.masterGain.gain.setValueAtTime(0, t);
          graph.masterGain.gain.linearRampToValueAtTime(volume, t + 0.2);
          activeRef.current = true;
        });
      } else if (graphRef.current) {
        // Immediately mark inactive so secondary effects (volume, panning) stop.
        activeRef.current = false;
        const graph = graphRef.current;
        const { ctx, masterGain } = graph;
        const t = ctx.currentTime;

        // Fade out over 200ms to avoid a click on stop.
        masterGain.gain.cancelScheduledValues(t);
        masterGain.gain.setValueAtTime(masterGain.gain.value, t);
        masterGain.gain.linearRampToValueAtTime(0, t + 0.2);

        // After the fade, fully destroy the graph — stops oscillators and
        // closes the AudioContext. This is more reliable than ctx.suspend(),
        // which browsers sometimes ignore.
        setTimeout(() => {
          if (cancelled) return;
          teardownGraph(graph);
          // Only null the ref if it still points to this graph (not a new one).
          if (graphRef.current === graph) graphRef.current = null;
        }, 250);
      }

      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRunning, audioOn, preset]);

    // ── Live volume update ───────────────────────────────────────────────────
    useEffect(() => {
      if (!activeRef.current || !graphRef.current) return;
      const { masterGain, ctx } = graphRef.current;
      masterGain.gain.setTargetAtTime(volume, ctx.currentTime, 0.05);
    }, [volume]);

    // ── Live preset frequency update (while playing) ─────────────────────────
    // Note: since we rebuild the graph on each start, this only applies when
    // the user switches presets mid-session without stopping.
    useEffect(() => {
      if (!activeRef.current || !graphRef.current || preset === "off") return;
      const { oscL, oscR, ctx } = graphRef.current;
      const { carrier, beat } = AUDIO_PRESETS[preset as Exclude<AudioPreset, "off">];
      // Slow crossfade (100ms) so the frequency shift is perceptible but smooth
      oscL.frequency.setTargetAtTime(carrier, ctx.currentTime, 0.1);
      oscR.frequency.setTargetAtTime(carrier + beat, ctx.currentTime, 0.1);
    }, [preset]);

    // ── Cleanup on unmount ───────────────────────────────────────────────────
    useEffect(() => {
      return () => {
        if (graphRef.current) {
          teardownGraph(graphRef.current);
          graphRef.current = null;
        }
      };
    }, []);

    return null; // audio-only — no DOM output
  }
);

export default AudioEngine;
